import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import ReportsSubNav from "@/components/ReportsSubNav";
import ScopeToggle from "@/components/ScopeToggle";
import { usePipelineCtx } from "@/lib/pipelineContext.jsx";
import { SkeletonTile, SkeletonRows } from "@/components/Skeleton";

const PERIOD_OPTS = [
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
  { value: "all", label: "All Time" },
];

function fmtTokens(n) {
  const v = Number(n) || 0;
  if (v < 1000) return String(v);
  if (v < 1_000_000) return `${(v / 1000).toFixed(1)}k`;
  return `${(v / 1_000_000).toFixed(2)}M`;
}

export default function AgentAnalytics() {
  const { pipelineId } = usePipelineCtx();
  const [period, setPeriod] = useState("30d");
  const [scope, setScope] = useState("pipeline"); // v5.7 Addition 22
  const [data, setData] = useState(null);

  useEffect(() => {
    if (scope === "pipeline" && !pipelineId) return;
    const params = { time_period: period, scope };
    if (scope === "pipeline") params.pipeline_id = pipelineId;
    api.get("/reports/agent-analytics", { params })
      .then(({ data }) => setData(data));
  }, [period, scope, pipelineId]);

  return (
    <div className="p-8">
      <div className="cw-caption uppercase tracking-widest">Reporting</div>
      <div className="mt-1 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <h1 className="cw-h1" data-testid="report-agents-title">Agent Run Analytics</h1>
          <span className="cw-badge" style={{ background: "var(--cw-primary)", color: "white" }}
            data-testid="role-scope-pill">
            VC Admin · Senior VC
          </span>
        </div>
        <div className="inline-flex items-center gap-3">
          <ScopeToggle value={scope} onChange={setScope} />
          <label className="cw-caption">Period</label>
          <select value={period} onChange={(e) => setPeriod(e.target.value)}
            className="h-9 px-3 rounded-md border text-sm bg-white"
            style={{ borderColor: "var(--cw-border)" }}
            data-testid="period-selector">
            {PERIOD_OPTS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-4"><ReportsSubNav /></div>

      {!data ? (
        <div className="mt-6" data-testid="agent-analytics-skeleton">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[0,1,2,3].map(i => <SkeletonTile key={i} />)}
          </div>
          <div className="mt-6 cw-card p-5">
            <SkeletonRows rows={6} />
          </div>
        </div>
      ) : (
        <>
          {/* KPI tiles */}
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
            <KTile label="Total Runs" value={data.total_runs}
              subLabel="Agent runs this period" subColor="var(--cw-muted)"
              testid="tile-total-runs" />
            <KTile
              label="Completed"
              value={data.total_runs === 0 ? "—" : data.completed}
              subLabel={data.total_runs === 0 ? "No runs this period" : `${data.success_rate}% success rate`}
              subColor={data.total_runs === 0 ? "var(--cw-muted)" : "var(--cw-success)"}
              valueColor={data.total_runs === 0 ? "var(--cw-muted)" : "var(--cw-success)"}
              testid="tile-completed"
            />
            <KTile label="Failed" value={data.failed}
              subLabel="Agent runs that did not complete"
              subColor="var(--cw-error)" valueColor="var(--cw-error)"
              testid="tile-failed" />
            <KTile
              label="Tokens Used"
              value={fmtTokens(data.total_tokens)}
              subLabel={
                data.workspace_cap
                  ? `of ${(data.workspace_cap / 1_000_000).toFixed(1)}M workspace cap`
                  : "No workspace cap set"
              }
              subColor="var(--cw-warning)" valueColor="var(--cw-warning)"
              testid="tile-tokens"
            />
          </div>

          {/* Two-column: runs by agent + budget */}
          <div className="mt-6 grid gap-4" style={{ gridTemplateColumns: "minmax(0, 55fr) minmax(0, 45fr)" }}>
            <RunsByAgent runs={data.runs_by_agent} />
          
          </div>

          {/* Run Depth */}
          <RunDepth depth={data.run_depth} />
        </>
      )}
    </div>
  );
}

function KTile({ label, value, subLabel, subColor, valueColor, testid }) {
  return (
    <div className="cw-card p-4" data-testid={testid}>
      <div className="text-xs" style={{ color: "var(--cw-muted)" }}>{label}</div>
      <div className="text-[28px] font-bold mt-1"
        style={{ color: valueColor || "var(--cw-primary-dark)" }}>
        {value}
      </div>
      <div className="text-xs mt-1" style={{ color: subColor }}>{subLabel}</div>
    </div>
  );
}

function RunsByAgent({ runs }) {
  const max = Math.max(...runs.map((r) => r.count), 1);
  return (
    <div className="cw-card p-5" data-testid="runs-by-agent-card">
      <h3 className="cw-h3">Runs by Agent</h3>
      {runs.length === 0 ? (
        <div className="mt-8 text-center text-[var(--cw-muted)] text-sm">
          No agent runs in this period
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          {runs.map((r) => {
            const pct = (r.count / max) * 100;
            return (
              <div key={r.agent_id} className="grid grid-cols-12 items-center gap-3"
                data-testid={`runs-by-agent-row-${r.agent_name}`}>
                <div className="col-span-4 text-sm">{r.agent_name}</div>
                <div className="col-span-7 rounded h-6" style={{ background: "#F5F5F5" }}>
                  <div className="h-full rounded" style={{
                    width: `${pct}%`,
                    background: "var(--cw-primary)",
                    transition: "width 250ms ease",
                  }} />
                </div>
                <div className="col-span-1 text-sm text-right font-medium">{r.count}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function BudgetUtilisation({ budget }) {
  const rows = [];
  const w = budget.workspace;
  const wsUncapped = !w.cap;
  rows.push({
    label: "Workspace (monthly)",
    tokens: w.tokens_used,
    cap: w.cap,
    uncapped: wsUncapped,
    testid: "budget-row-workspace",
  });
  for (const u of budget.per_user) {
    rows.push({
      label: u.full_name,
      tokens: u.tokens_used,
      cap: u.cap,
      uncapped: false,
      testid: `budget-row-${u.full_name}`,
    });
  }
  const hasAny = rows.length > 0 && !(rows.length === 1 && rows[0].uncapped && !budget.per_user.length);

  const pctColor = (pct) => {
    if (pct >= 100) return "var(--cw-error)";
    if (pct >= 80) return "var(--cw-warning)";
    return "var(--cw-success)";
  };

  return (
    <div className="cw-card p-5" data-testid="budget-card">
      <h3 className="cw-h3">Budget Utilisation</h3>
      {!hasAny ? (
        <div className="mt-8 text-center text-[var(--cw-muted)] text-sm">
          No budget caps configured. Set caps in Platform Management.
        </div>
      ) : (
        <div className="mt-4 space-y-4">
          {rows.map((r) => {
            const pct = r.uncapped ? 20 : Math.min(100, Math.round(((r.tokens || 0) / (r.cap || 1)) * 100));
            const barColor = r.uncapped ? "#D9D9D9" : pctColor(pct);
            return (
              <div key={r.testid} data-testid={r.testid}>
                <div className="flex justify-between items-baseline">
                  <div className="text-sm">{r.label}</div>
                  <div className="text-xs text-[var(--cw-muted)]">
                    {fmtTokens(r.tokens)} / {r.uncapped ? "No cap set" : fmtTokens(r.cap)}
                  </div>
                </div>
                <div className="mt-1 h-2 rounded" style={{ background: "#F5F5F5" }}>
                  <div className="h-full rounded" style={{
                    width: `${pct}%`,
                    background: barColor,
                    transition: "width 250ms ease",
                  }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div className="cw-caption mt-4">
        Alert at 80% utilisation. Agent runs blocked at 100%. VC Admin can reset caps at any time.
      </div>
    </div>
  );
}

function RunDepth({ depth }) {
  const values = [depth.full_depth || 0, depth.high_level || 0, depth.monitoring || 0];
  const max = Math.max(...values, 1);
  const bars = [
    { key: "full_depth", label: "Full depth", count: depth.full_depth || 0, color: "var(--cw-primary)" },
    { key: "high_level", label: "High level", count: depth.high_level || 0, color: "var(--cw-secondary)" },
    { key: "monitoring", label: "Monitoring", count: depth.monitoring || 0, color: "#D9D9D9" },
  ];
  return (
    <div className="cw-card mt-4 p-5" data-testid="run-depth-card">
      <h3 className="cw-h3">Run Depth Distribution</h3>
      <div className="mt-6 grid grid-cols-3 gap-6 items-end h-56">
        {bars.map((b) => {
          const heightPct = Math.max(3, (b.count / max) * 100);
          return (
            <div key={b.key} className="flex flex-col items-center justify-end h-full"
              data-testid={`depth-bar-${b.key}`}>
              <div className="text-sm font-bold" style={{ color: "var(--cw-primary-dark)" }}>{b.count}</div>
              <div className="w-full mt-2 rounded-t" style={{
                background: b.color,
                height: `${heightPct}%`,
                minHeight: 4,
                transition: "height 250ms ease",
              }} />
              <div className="text-xs text-[var(--cw-muted)] mt-2">{b.label}</div>
            </div>
          );
        })}
      </div>
      <div className="cw-caption mt-4">
        Full depth runs consume the most tokens and suit late-stage deals with rich data. High
        level and Monitoring depths use fewer tokens and are better for early screening and
        ongoing tracking.
      </div>
    </div>
  );
}
